import type { FC } from 'react';
import React, { useState } from 'react';
import {
  Button,
  CondensedHeader,
  CondensedLarge,
  FluidButtonLayout,
  Form,
  Gutter,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  SectionHeader,
  ToastUtil,
} from '@axiom/ui';

import { TagType } from '../../enums/generated/tag-type';
import { LegacyTagsApi } from '../../api/legacy-tags-api';
import type { Tag } from '../../types/generated/tag';

import { AdminModalTagsFormSchema } from './admin-modal-tags-form-schema';

export type AdminModalTagsFormProps = {
  name?: string;
  tags: Tag[];
  selected: Partial<Tag>;
  onClose: () => void;
};

export const AdminModalTagsForm: FC<AdminModalTagsFormProps> = ({
  name,
  tags,
  selected,
  onClose,
}) => {
  const [shouldConfirmSave, setShouldConfirmSave] = useState(false);
  const isEditing = (selected.id?.length ?? 0) > 0;

  return (
    <Form
      name={name || 'TAG_FORM'}
      initialValues={{ name: selected.name ?? undefined }}
      schema={AdminModalTagsFormSchema}
      onSubmit={async changedData => {
        const tagValue = { ...selected, ...changedData };
        const tagName = tagValue.name;

        if (!tagName) {
          return;
        }

        const tagExistsWithName = tags.some(
          tag =>
            tag.name?.toLowerCase() === tagName.toLowerCase() &&
            tag.id !== tagValue.id
        );

        if (tagExistsWithName) {
          ToastUtil.add({
            name: 'TAG_EXISTS',
            type: 'error',
            dismissible: true,
            children: (
              <>
                <CondensedHeader>Tag already exists</CondensedHeader>
                <CondensedLarge>
                  A tag already exists with name &quot;{tagName}&quot;
                </CondensedLarge>
              </>
            ),
          });
          return;
        }

        try {
          await (selected.id
            ? LegacyTagsApi.updateTag({
                id: selected.id,
                body: { name: tagName },
              })
            : LegacyTagsApi.createTags({
                body: [{ name: tagName, type: TagType.Tag }],
              }));

          ToastUtil.add({
            name: 'TAG_SAVE_SUCCESS',
            type: 'info',
            dismissible: true,
            children: (
              <CondensedHeader>
                Tag {isEditing ? 'updated' : 'created'} successfully
              </CondensedHeader>
            ),
          });

          onClose();
        } catch {
          ToastUtil.add({
            name: 'TAG_SAVE_ERROR',
            type: 'error',
            dismissible: true,
            children: (
              <>
                <CondensedHeader>Failed to save tag</CondensedHeader>
                <CondensedLarge>Please try again later.</CondensedLarge>
              </>
            ),
          });
        }
      }}
    >
      {({ fireSubmit }) => (
        <Gutter name="TAG_MANAGEMENT_FORM">
          <Gutter bottom="16px">
            <SectionHeader>
              {isEditing ? 'Edit Tag' : 'Create New Tag'}
            </SectionHeader>
          </Gutter>
          <Input name="name" label={isEditing ? 'Tag' : 'New Tag'} />

          <Gutter bottom="16px" />

          <FluidButtonLayout>
            {isEditing ? (
              <Button
                name="CANCEL_BUTTON"
                onClick={() => {
                  onClose();
                }}
                variation="outline"
              >
                Cancel
              </Button>
            ) : null}
            <Button
              name="SUBMIT_BUTTON"
              onClick={() => {
                if (isEditing) {
                  setShouldConfirmSave(true);
                } else {
                  void fireSubmit();
                }
              }}
            >
              Save
            </Button>
          </FluidButtonLayout>

          {shouldConfirmSave ? (
            <Modal size="confirm">
              <ModalHeader
                onClose={() => {
                  setShouldConfirmSave(false);
                }}
              >
                Edit Tag
              </ModalHeader>
              <ModalSection>
                <Paragraph>
                  You have selected to edit this tag. Doing so will edit it on
                  all opportunities, talent profiles and saved searches where
                  this has been selected.
                </Paragraph>
              </ModalSection>
              <ModalFooter>
                <Button
                  variation="outline"
                  onClick={() => {
                    setShouldConfirmSave(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  name="CONFIRM_SAVE_BUTTON"
                  onClick={() => {
                    setShouldConfirmSave(false);
                    void fireSubmit();
                  }}
                >
                  Save
                </Button>
              </ModalFooter>
            </Modal>
          ) : null}
        </Gutter>
      )}
    </Form>
  );
};
